import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

function mergeProductTableSx(...items) {
  return items.flatMap((item) => (Array.isArray(item) ? item : [item])).filter(Boolean);
}

export function getProductTableMinWidth(columns) {
  return columns.reduce((total, column) => total + Number(column.width || 0), 0);
}

export const productTableTextClipSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export function formatProductPaginationRange(total, page = 0, rowsPerPage = total, formatValue = (value) => value) {
  if (!total) return "0 de 0";

  const start = page * rowsPerPage + 1;
  const end = Math.min(total, start + rowsPerPage - 1);

  return `${formatValue(start)}-${formatValue(end)} de ${formatValue(total)}`;
}

export function ProductTableText({
  children,
  color,
  fontSize = 14,
  fontWeight = 400,
  lineHeight = "21px",
  muted = false,
  sx,
  title,
}) {
  const resolvedTitle = title ?? (typeof children === "string" ? children : undefined);

  return (
    <Typography
      title={resolvedTitle}
      sx={mergeProductTableSx(
        {
          color: color || (muted ? "text.secondary" : "text.primary"),
          fontSize,
          fontWeight,
          letterSpacing: 0,
          lineHeight,
          minWidth: 0,
          ...productTableTextClipSx,
        },
        sx,
      )}
    >
      {children || "-"}
    </Typography>
  );
}

export function ProductTableRoot({
  borderTop = true,
  children,
  component = "section",
  sx,
}) {
  return (
    <Box
      component={component}
      sx={mergeProductTableSx(
        (theme) => ({
          bgcolor: theme.palette.background.paper,
          borderTop: borderTop ? `1px solid ${theme.palette.border.default}` : 0,
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
        }),
        sx,
      )}
    >
      {children}
    </Box>
  );
}

export function ProductTableScroll({
  children,
  contentPaddingX = 0,
  fixedHeader = false,
  sx,
}) {
  return (
    <Box
      sx={mergeProductTableSx(
        (theme) => ({
          flex: "1 1 auto",
          minHeight: 0,
          overflowX: "auto",
          overflowY: fixedHeader ? "auto" : "hidden",
          px: contentPaddingX,
          scrollbarColor: `${theme.palette.custom.gray[400]} ${theme.palette.custom.gray[100]}`,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.custom.gray[400],
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.custom.gray[100],
          },
        }),
        sx,
      )}
    >
      {children}
    </Box>
  );
}

export function ProductTableElement({
  children,
  columns,
  component = "table",
  minWidth,
  sx,
}) {
  const resolvedMinWidth = minWidth || getProductTableMinWidth(columns);

  return (
    <Box
      component={component}
      sx={mergeProductTableSx(
        {
          bgcolor: "background.paper",
          borderCollapse: "collapse",
          minWidth: resolvedMinWidth,
          tableLayout: "fixed",
          width: "100%",
          "& td, & th": {
            boxSizing: "border-box",
          },
        },
        sx,
      )}
    >
      {children}
    </Box>
  );
}

export function ProductTableColumnGroup({ columns }) {
  return (
    <Box component="colgroup">
      {columns.map((column) => (
        <Box component="col" key={column.id} sx={{ width: column.width }} />
      ))}
    </Box>
  );
}

export function ProductTableHeader({
  columns,
  firstCellSx,
  getCellSx,
  lastCellSx,
  renderColumn,
  sticky = false,
  stickyTop = 0,
}) {
  return (
    <Box component="thead">
      <Box component="tr">
        {columns.map((column, index) => {
          const isFirst = index === 0;
          const isLast = index === columns.length - 1;

          return (
            <Box
              component="th"
              key={column.id}
              sx={mergeProductTableSx(
                (theme) => ({
                  bgcolor: theme.palette.surface.muted,
                  borderBottom: `1px solid ${theme.palette.border.default}`,
                  color: "text.secondary",
                  fontSize: 14,
                  fontWeight: 600,
                  height: 56,
                  letterSpacing: 0,
                  lineHeight: "21px",
                  position: sticky ? "sticky" : "static",
                  px: 2,
                  py: 0,
                  top: sticky ? stickyTop : undefined,
                  textAlign: column.headerAlign || column.align || "left",
                  verticalAlign: "middle",
                  whiteSpace: "nowrap",
                  zIndex: sticky ? 2 : "auto",
                }),
                isFirst ? firstCellSx : null,
                isLast ? lastCellSx : null,
                getCellSx?.(column, index),
                column.headerSx,
              )}
            >
              {renderColumn ? renderColumn(column, index) : column.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function ProductTableEmptyRow({
  children,
  colSpan,
  height = 240,
  message = "Nenhum registro encontrado.",
  sx,
}) {
  return (
    <Box component="tr">
      <Box
        component="td"
        colSpan={colSpan}
        sx={mergeProductTableSx(
          {
            color: "text.primary",
            fontSize: 16,
            fontWeight: 700,
            height,
            px: 2,
            textAlign: "center",
            verticalAlign: "middle",
          },
          sx,
        )}
      >
        {children || message}
      </Box>
    </Box>
  );
}

export function ProductTablePagination({
  disabled = false,
  formatValue,
  height = 72,
  onPageChange,
  page = 0,
  rowsPerPage,
  sx,
  total = 0,
}) {
  const safeRowsPerPage = rowsPerPage || Math.max(total, 1);
  const lastPage = Math.max(0, Math.ceil(total / safeRowsPerPage) - 1);
  const safePage = Math.min(page, lastPage);

  return (
    <Box
      sx={mergeProductTableSx(
        (theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.border.default}`,
          color: "text.secondary",
          display: "flex",
          flexShrink: 0,
          gap: 2,
          height,
          justifyContent: "center",
          mt: "auto",
          px: 2,
        }),
        sx,
      )}
    >
      <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 500 }}>
        {formatProductPaginationRange(total, safePage, safeRowsPerPage, formatValue)}
      </Typography>
      <Stack direction="row" spacing={0}>
        <IconButton
          aria-label="Página anterior"
          disabled={disabled || safePage <= 0}
          onClick={(event) => onPageChange?.(event, safePage - 1)}
          size="small"
          sx={{
            color: "text.secondary",
            height: 30,
            width: 30,
            "&.Mui-disabled": {
              color: "text.disabled",
            },
          }}
        >
          <KeyboardArrowLeftRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          aria-label="Próxima página"
          disabled={disabled || safePage >= lastPage}
          onClick={(event) => onPageChange?.(event, safePage + 1)}
          size="small"
          sx={{
            color: "text.secondary",
            height: 30,
            width: 30,
            "&.Mui-disabled": {
              color: "text.disabled",
            },
          }}
        >
          <KeyboardArrowRightRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Box>
  );
}
